import React, {useMemo, useRef} from 'react'
import {Bar} from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  type ChartOptions,
  type Plugin,
} from 'chart.js'
import type {OpiHourlySummary} from './_requests'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

const CONNECTED_COLOR = '#78d381'
const CONNECTED_HOVER = '#5fc46a'
const ABANDONED_COLOR = '#e67a73'
const ABANDONED_HOVER = '#d9655e'

type Props = {
  data: OpiHourlySummary[]
}

/** Draw % on stacked segments when the segment is tall enough. */
const stackedPercentLabels: Plugin<'bar'> = {
  id: 'opiStackedPercentLabels',
  afterDatasetsDraw(chart) {
    const {ctx, data} = chart
    const meta0 = chart.getDatasetMeta(0)
    const meta1 = chart.getDatasetMeta(1)
    if (!meta0?.data?.length) return

    ctx.save()
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = '#ffffff'
    ctx.font = 'bold 10px Manrope, sans-serif'

    for (let i = 0; i < meta0.data.length; i++) {
      const connected = Number(data.datasets[0]?.data[i] ?? 0)
      const abandoned = Number(data.datasets[1]?.data[i] ?? 0)
      const total = connected + abandoned
      if (total <= 0) continue

      const connectedPct = Math.round((connected / total) * 1000) / 10
      const abandonedPct = Math.round((abandoned / total) * 1000) / 10
      const minH = 16

      const bar0 = meta0.data[i]
      const bar1 = meta1?.data?.[i]

      if (bar0 && connected > 0) {
        const props = bar0.getProps(['x', 'y', 'base'], true) as {
          x: number
          y: number
          base: number
        }
        const {x, y, base} = props
        if (Math.abs(base - y) >= minH) {
          ctx.fillText(`${connectedPct}%`, x, (y + base) / 2)
        }
      }

      if (bar1 && abandoned > 0) {
        const props = bar1.getProps(['x', 'y', 'base'], true) as {
          x: number
          y: number
          base: number
        }
        const {x, y, base} = props
        if (Math.abs(base - y) >= minH) {
          ctx.fillText(`${abandonedPct}%`, x, (y + base) / 2)
        }
      }
    }
    ctx.restore()
  },
}

function useViewportWidth() {
  const [width, setWidth] = React.useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1200
  )
  React.useEffect(() => {
    const onResize = () => setWidth(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  return width
}

function formatHourLabel(hour: number, fallback: string): string {
  const d = new Date()
  d.setHours(hour, 0, 0, 0)
  try {
    return d.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'})
  } catch {
    return fallback
  }
}

export const HourlyCallsChart: React.FC<Props> = ({data}) => {
  const viewportWidth = useViewportWidth()
  const isMobile = viewportWidth <= 768
  const isSmall = viewportWidth <= 480
  const chartRef = useRef<ChartJS<'bar'>>(null)

  const rows = useMemo(() => [...data].sort((a, b) => a.hour - b.hour), [data])

  const chartData = useMemo(
    () => ({
      labels: rows.map((d) => formatHourLabel(d.hour, d.hourLabel)),
      datasets: [
        {
          label: 'Connected',
          data: rows.map((d) => d.connected),
          backgroundColor: CONNECTED_COLOR,
          hoverBackgroundColor: CONNECTED_HOVER,
          stack: 'calls',
          categoryPercentage: 0.65,
          barPercentage: 0.9,
          borderRadius: {topLeft: 0, topRight: 0, bottomLeft: 2, bottomRight: 2},
          borderSkipped: false as const,
        },
        {
          label: 'Abandoned',
          data: rows.map((d) => d.abandoned),
          backgroundColor: ABANDONED_COLOR,
          hoverBackgroundColor: ABANDONED_HOVER,
          stack: 'calls',
          categoryPercentage: 0.65,
          barPercentage: 0.9,
          borderRadius: {topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0},
          borderSkipped: false as const,
        },
      ],
    }),
    [rows]
  )

  const options: ChartOptions<'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 650,
        easing: 'easeOutQuart',
      },
      layout: {
        padding: {top: 12, right: 8, bottom: 4, left: 4},
      },
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {display: false},
        tooltip: {
          enabled: true,
          mode: 'index',
          intersect: false,
          backgroundColor: '#ffffff',
          titleColor: '#171a1f',
          bodyColor: '#565d6d',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          titleFont: {size: 13, weight: 'bold'},
          bodyFont: {size: 12},
          padding: 12,
          cornerRadius: 8,
          displayColors: false,
          caretPadding: 8,
          callbacks: {
            title(items) {
              return items[0]?.label ?? ''
            },
            label(ctx) {
              // Emit the full tooltip once (on Connected dataset only)
              if (ctx.datasetIndex !== 0) return null as unknown as string
              const row = rows[ctx.dataIndex]
              if (!row) return null as unknown as string
              return [
                `Total: ${row.totalCalls}`,
                `Connected: ${row.connected}`,
                `Abandoned: ${row.abandoned}`,
                `Success: ${Number(row.successPercent).toFixed(1)}%`,
              ] as unknown as string
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: {display: false},
          border: {display: false},
          ticks: {
            color: '#9095a1',
            font: {size: isSmall ? 9 : isMobile ? 10 : 12},
            maxRotation: isMobile ? 40 : 0,
            minRotation: 0,
            autoSkip: false,
            padding: 8,
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          grace: '6%',
          border: {display: false},
          ticks: {
            color: '#9095a1',
            font: {size: isSmall ? 9 : 11},
            precision: 0,
            padding: 8,
          },
          grid: {
            color: '#e5e7eb',
            // Chart.js 3: dotted horizontal guides like the design
            borderDash: [4, 4],
            drawTicks: false,
          },
        },
      },
      onHover: (event, elements) => {
        const canvas = (event?.native?.target ?? undefined) as HTMLCanvasElement | undefined
        if (canvas) canvas.style.cursor = elements.length ? 'pointer' : 'default'
      },
    }),
    [isMobile, isSmall, rows]
  )

  return (
    <div className='opi-chart-card'>
      <h3 className='opi-chart-card__title'>Hourly Trend — Calls &amp; Success Rate</h3>

      {rows.length === 0 ? (
        <div className='opi-empty'>No call data for the selected filters.</div>
      ) : (
        <div className='opi-chart-card__canvas-wrap'>
          <div className='opi-chart-card__canvas'>
            <Bar
              ref={chartRef as any}
              data={chartData}
              options={options}
              plugins={[stackedPercentLabels]}
            />
          </div>
        </div>
      )}

      <div className='opi-chart-card__legend'>
        <div className='opi-chart-card__legend-item'>
          <span className='opi-chart-card__swatch opi-chart-card__swatch--connected' />
          <span>Connected</span>
        </div>
        <div className='opi-chart-card__legend-item'>
          <span className='opi-chart-card__swatch opi-chart-card__swatch--abandoned' />
          <span>Abandoned</span>
        </div>
      </div>
    </div>
  )
}
